import { useForm } from '@tanstack/react-form'
import { capitalize } from 'lodash'
import { CheckCircle2, Fullscreen, Mail, Plus } from 'lucide-react'
import { useEffect, useState } from 'react'
import { donationMethods } from '../constants/donationMethods'
import {
  useSendRegistrationRequestMutation,
  useVerifyDonationMutation,
} from '../hooks'
import { DonationUpdateSchema } from '../schema/donationSchema'
import { useDonationsStore } from '../store/donationsStore'
import { EmailPreview } from './EmailPreview'
import type { ApiError } from '@/types/api'
import type { DonationUpdateData } from '../schema/donationSchema'
import type { DonationMethod } from '../types/donation'
import { AutoCompleteSelect } from '@/components/custom/auto-complete-select'
import CurrencyInput from '@/components/custom/currency-input'
import DatePicker from '@/components/custom/date-picker'
import { Modal } from '@/components/custom/modal'
import { FormInput } from '@/components/custom/form-input'
import { FormTextarea } from '@/components/custom/form-textarea'
import LoadingButton from '@/components/custom/loading-button'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { useChurchesStore } from '@/features/churches/store/churchesStore'
import { MemberForm } from '@/features/members/components/MemberForm'
import { useMembersStore } from '@/features/members/store/membersStore'
import { notifyRequestError } from '@/utils/notifyRequestError'
import { formatDate, transformDateToTimezone } from '@/utils/timeZoneUtils'
import { useAuthStore } from '@/features/auth/store/authStore'
import { usePermissions } from '@/utils/permissions'
import { useIsMobile } from '@/hooks'

const getMethodHelper = (method: string) => {
  const methodKey = method as DonationMethod
  const methodMap: Record<DonationMethod, string> = {
    'e-transfer': 'Reference Number',
    cheque: 'Cheque Number',
    deposit: 'Deposit Number',
    cash: '',
  }
  return methodMap[methodKey] || ''
}

export function PendingDonationForm({
  id,
  trigger,
  open,
  onClose,
  onVerified,
}: {
  id?: string | null
  trigger?: React.ReactNode
  open?: boolean
  onClose?: () => void
  onVerified?: (donationId: string) => void
}) {
  const isMobile = useIsMobile()
  const activeRole = useAuthStore((s) => s.activeRole)
  const { mutateAsync: verifyDonationMutation, isPending: isVerifying } =
    useVerifyDonationMutation()
  const {
    mutate: sendRegistrationRequest,
    isPending: sendingRegistrationRequest,
  } = useSendRegistrationRequestMutation()

  const [openMemberDialog, setOpenMemberDialog] = useState(false)
  const [disableForm, setDisableForm] = useState(true)
  const [showEmailPreviewFullscreen, setShowEmailPreviewFullscreen] =
    useState(false)

  const donations = useDonationsStore((s) => s.donations)
  const initial = donations.find((d) => d._id === id)
  const churches = useChurchesStore((s) => s.churches)
  const members = useMembersStore((s) => s.members)

  const isUnmatchedSender =
    initial?.source === 'etransfer_auto' && !initial.donorId
  const canEmailSender =
    isUnmatchedSender &&
    (activeRole === 'SYSTEM_ADMIN' || activeRole === 'DEVELOPER')

  // Get permissions for church filtering
  const permissions = usePermissions()

  // Filter churches based on user's role and assignments
  const assignedChurchIds = permissions.getUserAssignedChurches()
  const filteredMembers = (
    assignedChurchIds.length === 0
      ? members // Super admin sees all churches
      : members.filter((member) => assignedChurchIds.includes(member.churchId))
  ).sort((a, b) => a.name.localeCompare(b.name))
  const form = useForm({
    defaultValues: {
      _id: initial?._id || '',
      churchId: initial?.churchId || '',
      donorId: initial?.donorId || '',
      amount: initial?.amount || 0,
      date: initial?.date || '',
      donationRef: initial?.donationRef || '',
      notes: initial?.notes || '',
      method: initial?.method || '',
      isRegistrationFee: initial?.isRegistrationFee ?? false,
    },
    onSubmit: async ({ value }) => {
      const transformedValue = {
        ...value,
        date: transformDateToTimezone(value.date),
      } as DonationUpdateData

      try {
        await verifyDonationMutation(transformedValue)
        handleClose()
        setTimeout(() => {
          onVerified?.(transformedValue._id)
        }, 0)
      } catch (error) {
        console.log('Error occurred while submitting form:', error)
        notifyRequestError(
          error as ApiError,
          'Error occurred while submitting donation form',
        )
      }
    },
  })

  const handleClose = () => {
    onClose?.()
    form.reset()
    setDisableForm(true)
  }

  const handleEdit = () => {
    setDisableForm(false)
  }

  // Reset form state when dialog opens
  useEffect(() => {
    if (open) {
      setDisableForm(true)
    }
  }, [open])

  return (
    <Modal
      open={open}
      onClose={handleClose}
      trigger={trigger}
      title={`Verify Donation #${initial?.receiptNumber}`}
      direction="right"
      contentClassName={
        isMobile
          ? '!max-h-[100vh] !h-[100vh] min-w-[98vw] !w-[98vw]'
          : '!max-w-[90vw] !w-[90vw] !min-w-[90vw]'
      }
    >
      {initial?.status === 'pending' && (
        <div className="mb-4 text-sm text-center w-full">
          {initial.churchId && initial.donorId ? (
            // Auto-matched donation with valid church and donor
            <div className="text-muted-foreground">
              <p className="mt-1">
                Click <span className="font-semibold">Verify</span> to confirm
                the donation details. If you need to change any information,
                click <span className="font-semibold">Edit</span> and update the
                fields below.
              </p>
            </div>
          ) : (
            // Donation requires manual assignment
            <div className="text-amber-700 bg-amber-50 p-3 rounded-lg border border-amber-200">
              <p className="font-semibold flex justify-center gap-2">
                Manual Assignment Required -{' '}
                {!initial.churchId && !initial.donorId
                  ? 'Church and donor could not be automatically identified.'
                  : !initial.churchId
                    ? 'Church could not be automatically identified.'
                    : 'Donor could not be automatically identified.'}
              </p>
              {!isMobile && (
                <p className="mt-1">
                  Please click <span className="font-semibold">Edit</span> to
                  manually select the correct{' '}
                  {!initial.churchId && !initial.donorId
                    ? 'church and donor'
                    : !initial.churchId
                      ? 'church'
                      : 'donor'}
                  , then click <span className="font-semibold">Verify</span> to
                  complete the donation.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Unmatched Sender Information */}
      {isUnmatchedSender && (initial.emailFrom || initial.emailSubject) && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-amber-700 mb-3 flex items-center justify-center gap-2">
            <Mail className="h-4 w-4" />
            Unmatched Sender Information
          </h3>
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            {/* Sender info */}
            <div className="space-y-1 text-sm flex-1">
              {initial.emailFrom && (
                <p className="text-amber-700">
                  <span className="font-medium">Email: </span>
                  {initial.emailFrom}
                </p>
              )}
              {initial.emailSubject && (
                <p className="text-amber-700">
                  <span className="font-medium">Subject: </span>
                  {initial.emailSubject}
                </p>
              )}
            </div>

            {/* Action section: separator + sent badge + button */}
            {(initial.registrationEmailSent || canEmailSender) && (
              <div className="flex flex-col items-start lg:items-end gap-2 shrink-0 lg:border-l lg:border-amber-200 lg:pl-4">
                <div className="w-full border-t border-amber-200 lg:hidden" />
                {initial.registrationEmailSent &&
                  initial.registrationEmailSentAt && (
                    <div className="inline-flex items-center gap-1.5 text-xs bg-green-100 text-green-800 border border-green-200 rounded-full px-2.5 py-1">
                      <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                      <span>
                        Sent{' '}
                        {formatDate(
                          initial.registrationEmailSentAt,
                          'MMM D, YYYY [at] h:mm A',
                        )}
                      </span>
                    </div>
                  )}
                {canEmailSender && (
                  <div className="flex flex-col items-start lg:items-end gap-1">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => sendRegistrationRequest(initial._id)}
                      disabled={sendingRegistrationRequest || !initial.emailFrom}
                      className="flex items-center gap-2 border-amber-400 text-amber-700 hover:bg-amber-100"
                    >
                      <Mail className="h-4 w-4" />
                      {initial.registrationEmailSent
                        ? 'Resend Registration Request'
                        : 'Request Registration Info'}
                    </Button>
                    <p className="text-xs text-amber-700 lg:text-right lg:max-w-50">
                      Sends an email asking the sender to provide their name,
                      address, and church details.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main Layout */}
      <div className={isMobile ? '' : 'lg:flex lg:gap-6'}>
        {/* Form Section */}
        <div className="space-y-4 lg:min-w-[50%] flex-1">
          <form
            id="donation-form"
            onSubmit={(e) => {
              e.preventDefault()
              e.stopPropagation()
              form.handleSubmit()
            }}
            className="space-y-2"
          >
            <form.Subscribe
              children={() => {
                return (
                  <form.Field
                    name="donorId"
                    validators={{
                      onChange: ({ value }) => {
                        const selectedMember = members.find(
                          (m) => m._id === value,
                        )
                        if (selectedMember?.churchId) {
                          form.setFieldValue(
                            'churchId',
                            selectedMember.churchId,
                          )
                        }
                      },
                      onBlur: ({ value }) => {
                        const result =
                          DonationUpdateSchema.shape.donorId.safeParse(value)
                        return result.success
                          ? undefined
                          : result.error.errors[0].message
                      },
                    }}
                    children={(field) => {
                      return (
                        <div className="gap-0">
                          <AutoCompleteSelect
                            field={field}
                            label="Donor"
                            id={field.name}
                            name={field.name}
                            options={filteredMembers.map((member) => ({
                              value: member._id,
                              label: member.name,
                            }))}
                            required
                            disabled={disableForm}
                            addDialog={
                              <MemberForm
                                type="add"
                                onClose={() => setOpenMemberDialog(false)}
                                open={openMemberDialog}
                                trigger={
                                  <Button
                                    onClick={() => {
                                      setOpenMemberDialog(true)
                                    }}
                                  >
                                    <Plus /> Add Member
                                  </Button>
                                }
                              />
                            }
                          />
                        </div>
                      )
                    }}
                  />
                )
              }}
            />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <form.Field
                name="amount"
                validators={{
                  onBlur: ({ value }) => {
                    const result =
                      DonationUpdateSchema.shape.amount.safeParse(value)
                    return result.success
                      ? undefined
                      : result.error.errors[0].message
                  },
                }}
                children={(field) => {
                  return (
                    <div className="gap-0">
                      <CurrencyInput
                        field={field}
                        label="Amount"
                        id={field.name}
                        name={field.name}
                        className="text-right"
                        required
                        disabled={disableForm}
                      />
                    </div>
                  )
                }}
              />

              <form.Field
                name="date"
                children={(field) => (
                  <div className="gap-0">
                    <DatePicker
                      field={field}
                      label="Date"
                      id={field.name}
                      name={field.name}
                      startYear={2000}
                      disableFutureDates
                      disabled={disableForm}
                      required
                    />
                  </div>
                )}
              />
            </div>

            <form.Subscribe
              selector={(state) => [state.values.method]}
              children={([method]) => (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div
                    className={
                      !method || method === 'cash'
                        ? 'col-span-1 lg:col-span-2'
                        : 'col-span-1'
                    }
                  >
                    <form.Field
                      name="method"
                      validators={{
                        onBlur: ({ value }) => {
                          const result =
                            DonationUpdateSchema.shape.method.safeParse(value)
                          return result.success
                            ? undefined
                            : result.error.errors[0].message
                        },
                      }}
                      children={(field) => {
                        return (
                          <div className="gap-0">
                            <AutoCompleteSelect
                              field={field}
                              label="Donation Method"
                              id={field.name}
                              name={field.name}
                              options={Array.from(donationMethods).map((m) => ({
                                value: m,
                                label: capitalize(m),
                              }))}
                              required
                              disabled={disableForm}
                            />
                          </div>
                        )
                      }}
                    />
                  </div>

                  {!method || method === 'cash' ? null : (
                    <div className="col-span-1">
                      <form.Field
                        name="donationRef"
                        validators={{
                          onBlur: ({ value }) => {
                            if (!method || method === 'cash') return undefined

                            const result =
                              DonationUpdateSchema.shape.donationRef.safeParse(
                                value,
                              )
                            return result.success
                              ? undefined
                              : result.error.errors[0].message
                          },
                        }}
                        children={(field) => {
                          return (
                            <div className="gap-0">
                              <FormInput
                                field={field}
                                label={getMethodHelper(method)}
                                id={field.name}
                                name={field.name}
                                required
                                disabled={disableForm}
                              />
                            </div>
                          )
                        }}
                      />
                    </div>
                  )}
                </div>
              )}
            />

            <form.Field
              name="notes"
              validators={{
                onBlur: ({ value }) => {
                  const result =
                    DonationUpdateSchema.shape.notes.safeParse(value)
                  return result.success
                    ? undefined
                    : result.error.errors[0].message
                },
              }}
              children={(field) => (
                <div className="gap-0">
                  <FormTextarea
                    field={field}
                    label="Notes"
                    id={field.name}
                    name={field.name}
                    disabled={disableForm}
                  />
                </div>
              )}
            />

            <form.Field
              name="isRegistrationFee"
              children={(field) => (
                <div className="flex items-start gap-3 pt-1">
                  <Checkbox
                    id={field.name}
                    checked={!!field.state.value}
                    onCheckedChange={(v) => field.handleChange(Boolean(v))}
                    disabled={disableForm}
                  />
                  <div>
                    <Label htmlFor={field.name} className="font-medium">
                      Registration Fee
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Check if this is a seminar or event registration fee. No
                      receipt will be issued or included in the annual summary.
                    </p>
                  </div>
                </div>
              )}
            />

            <form.Subscribe
              selector={(state) => [
                state.canSubmit,
                state.values.churchId,
                state.values.donorId,
              ]}
              children={([canSubmit, churchId, donorId]) => {
                const isValidId = (
                  value: any,
                  type: 'churchId' | 'donorId',
                ): value is string => {
                  const list = type === 'churchId' ? churches : members
                  return (
                    typeof value === 'string' &&
                    value.trim().length > 0 &&
                    list.some((item) => item._id === value)
                  )
                }

                const hasValidChurchId = isValidId(churchId, 'churchId')
                const hasValidDonorId = isValidId(donorId, 'donorId')

                return (
                  <div className="flex gap-2 flex-col lg:flex-row">
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full lg:flex-1"
                      onClick={handleClose}
                    >
                      Cancel
                    </Button>
                    {disableForm &&
                      initial?.status === 'pending' &&
                      hasValidChurchId &&
                      hasValidDonorId && (
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full lg:flex-1"
                          onClick={handleEdit}
                        >
                          Edit
                        </Button>
                      )}
                    {initial?.status === 'pending' &&
                      disableForm &&
                      (!hasValidChurchId || !hasValidDonorId) && (
                        <LoadingButton
                          type="button"
                          className="w-full lg:flex-1"
                          isLoading={isVerifying}
                          disabled={!canSubmit || isVerifying}
                          onClick={handleEdit}
                        >
                          Edit Required
                        </LoadingButton>
                      )}
                    {initial?.status === 'pending' &&
                      hasValidChurchId &&
                      hasValidDonorId && (
                        <LoadingButton
                          type="submit"
                          className="w-full lg:flex-1"
                          isLoading={isVerifying}
                          disabled={!canSubmit || isVerifying}
                        >
                          {isVerifying ? 'Verifying...' : 'Verify'}
                        </LoadingButton>
                      )}
                    {/* Mobile Zoom Button */}
                    <div className="flex justify-end lg:hidden">
                      {/* Full Screen Email Preview Dialog */}
                      <Modal
                        open={showEmailPreviewFullscreen}
                        onClose={() => setShowEmailPreviewFullscreen(false)}
                        title="Email Preview"
                        contentClassName="!max-w-[100vw] !w-[100vw] px-0"
                        trigger={
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setShowEmailPreviewFullscreen(true)}
                            className="flex items-center gap-2 w-full"
                          >
                            <Fullscreen className="h-4 w-4" />
                            View Transfer Email
                          </Button>
                        }
                      >
                        <EmailPreview donation={initial || null} />
                      </Modal>
                    </div>
                  </div>
                )
              }}
            />
          </form>
        </div>

        {/* Email Preview Section - Desktop Only */}
        {!isMobile && (
          <div className="w-[50%]">
            <EmailPreview donation={initial || null} />
          </div>
        )}
      </div>
    </Modal>
  )
}
